import React, { useEffect, useState } from "react";
import { Mail, QrCode, Copy, Send, MessageSquare, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function RsvpsPage() {
  const [w, setW] = useState(null);
  const [guests, setGuests] = useState([]);
  const [showQr, setShowQr] = useState(false);
  const [sendingAll, setSendingAll] = useState(null);
  const [sendingGuest, setSendingGuest] = useState(null);
  const [cadence, setCadence] = useState({ enabled: false, channel: "email" });

  useEffect(() => {
    (async () => {
      const ww = await api.get("/weddings/mine");
      setW(ww.data);
      const g = await api.get(`/weddings/${ww.data.wedding_id}/guests`);
      setGuests(g.data);
      try {
        const c = await api.get("/reminders/cadence");
        setCadence({ enabled: !!c.data.enabled, channel: c.data.channel || "email" });
      } catch (e) { /* noop */ }
    })().catch(() => {});
  }, []);

  async function saveCadence(next) {
    setCadence(next);
    try {
      await api.post("/reminders/cadence", next);
      toast.success(next.enabled ? "Auto-cadence ON — guests get reminders 30/14/7/2 days out" : "Auto-cadence OFF");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function copyRsvpLink(g) {
    const url = `${window.location.origin}/site/${w.slug}/rsvp/${g.guest_id}`;
    navigator.clipboard.writeText(url);
    toast.success(`RSVP link copied for ${g.name}`);
  }

  async function sendOne(g, channel) {
    setSendingGuest(`${g.guest_id}-${channel}`);
    try {
      const { data } = await api.post(
        `/weddings/${w.wedding_id}/guests/${g.guest_id}/send-reminder`,
        { channel },
      );
      if (data.ok) {
        toast.success(data.mocked ? `MOCKED ${channel.toUpperCase()} reminder sent (enable provider in /admin)` : `${channel.toUpperCase()} reminder sent to ${g.name}`);
      } else {
        toast.error(data.message || "Failed to send");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingGuest(null);
    }
  }

  async function sendAll(channel) {
    if (!window.confirm(`Send ${channel.toUpperCase()} reminders to all pending guests?`)) return;
    setSendingAll(channel);
    try {
      const { data } = await api.post(
        `/weddings/${w.wedding_id}/send-bulk-reminders`,
        { channel },
      );
      const note = data.mocked ? " (MOCKED — enable provider in /admin)" : "";
      toast.success(`Sent ${data.sent} · Skipped ${data.skipped} · Failed ${data.failed}${note}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingAll(null);
    }
  }

  if (!w) return null;
  const att = guests.filter(g => g.rsvp_status === "attending");
  const dec = guests.filter(g => g.rsvp_status === "not_attending");
  const pen = guests.filter(g => g.rsvp_status === "pending");

  return (
    <div data-testid="rsvps-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">RSVP Tracker</p>
          <h1 className="font-serif font-light text-4xl">Responses in real time</h1>
        </div>
        <button data-testid="qr-rsvp-btn" onClick={() => setShowQr(true)} className="btn-outline-gold"><QrCode className="w-4 h-4" /> Public RSVP QR</button>
      </div>

      {/* Bulk reminder buttons */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="bulk-reminder-bar">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Nudge pending guests</p>
        <button
          onClick={() => sendAll("email")}
          disabled={sendingAll !== null}
          data-testid="bulk-email-btn"
          className="btn-outline-gold text-xs"
        >
          <Send className="w-3 h-3" /> {sendingAll === "email" ? "Sending…" : "Email all pending"}
        </button>
        <button
          onClick={() => sendAll("sms")}
          disabled={sendingAll !== null}
          data-testid="bulk-sms-btn"
          className="btn-outline-gold text-xs"
        >
          <MessageSquare className="w-3 h-3" /> {sendingAll === "sms" ? "Sending…" : "SMS all pending"}
        </button>

        <div className="flex-1" />

        {/* Auto-cadence toggle */}
        <div className="flex items-center gap-3" data-testid="cadence-control">
          <Zap className={`w-4 h-4 ${cadence.enabled ? "text-[var(--gold)]" : "text-[var(--muted)]"}`} />
          <select
            value={cadence.channel}
            onChange={(e) => saveCadence({ ...cadence, channel: e.target.value })}
            className="text-xs bg-transparent border border-[var(--line)] rounded-lg px-2 py-1.5"
            data-testid="cadence-channel"
          >
            <option value="email">Auto · Email</option>
            <option value="sms">Auto · SMS</option>
            <option value="both">Auto · Both</option>
          </select>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={cadence.enabled}
              onChange={(e) => saveCadence({ ...cadence, enabled: e.target.checked })}
              className="w-4 h-4 accent-[var(--gold)]"
              data-testid="cadence-enabled"
            />
            <span>30/14/7/2 days</span>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Attending", v: att.length, cls: "bg-[#e9f3e7]" },
          { label: "Pending", v: pen.length, cls: "bg-[var(--bg-warm)]" },
          { label: "Declined", v: dec.length, cls: "bg-[#f6e4e4]" },
        ].map((s) => (
          <div key={s.label} className={`rounded-2xl p-6 ${s.cls}`}>
            <p className="font-serif text-4xl">{s.v}</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl">
        <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Guest RSVP Links</p>
          <p className="text-xs text-[var(--muted)]">Send each guest their personal link</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">
              <tr><th className="px-5 py-3">Guest</th><th className="px-5 py-3">RSVP Status</th><th className="px-5 py-3">Meal</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {guests.map((g) => (
                <tr key={g.guest_id} className="border-t border-[var(--line)]">
                  <td className="px-5 py-4 font-medium">{g.name}</td>
                  <td className="px-5 py-4 capitalize">{g.rsvp_status.replace("_", " ")}</td>
                  <td className="px-5 py-4 text-[var(--muted)]">{g.meal_preference || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center gap-3 justify-end">
                      {g.email && (
                        <button
                          onClick={() => sendOne(g, "email")}
                          disabled={sendingGuest !== null}
                          title="Send email reminder"
                          data-testid={`send-email-${g.guest_id}`}
                          className="text-xs text-[var(--muted)] hover:text-[var(--gold)] flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                        </button>
                      )}
                      {g.phone && (
                        <button
                          onClick={() => sendOne(g, "sms")}
                          disabled={sendingGuest !== null}
                          title="Send SMS reminder"
                          data-testid={`send-sms-${g.guest_id}`}
                          className="text-xs text-[var(--muted)] hover:text-[var(--gold)] flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => copyRsvpLink(g)} className="text-xs text-[var(--gold)] flex items-center gap-1"><Copy className="w-3 h-3" /> Copy Link</button>
                    </div>
                  </td>
                </tr>
              ))}
              {guests.length === 0 && <tr><td colSpan="4" className="px-5 py-12 text-center text-[var(--muted)]">Add guests to start tracking RSVPs.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
            <p className="font-script text-2xl text-[var(--gold)]">RSVP at our wedding</p>
            <h3 className="font-serif text-2xl mb-6">{w.couple_names}</h3>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/qr?data=${encodeURIComponent(`${window.location.origin}/site/${w.slug}`)}`} alt="QR" className="mx-auto border border-[var(--line)] rounded-xl" />
            <p className="text-xs text-[var(--muted)] mt-4">/site/{w.slug}</p>
            <button onClick={() => setShowQr(false)} className="btn-outline-gold mt-6">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
